import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Select, Input, Divider } from "antd";
import { FilterOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useFilterFormAutoFocus } from "../../../../hooks/useFilterFormAutoFocus";

/** Match dashboard item-list FilterPanel accents */
const FILTER_MODAL_THEME = {
    primary: "#f45b68",
    editRowBg: "color-mix(in srgb, #f45b68 14%, var(--app-surface))",
    editRowBorder: "#f45b68",
};

const OPERATOR_SHORT = {
    gt: ">",
    lt: "<",
    gte: "≥",
    lte: "≤",
    eq: "=",
    contains: "contains",
};

const EXPENSE_TYPE_FILTER_OPTIONS = [
    { value: "Once", label: "Once" },
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
];

const AD_COST_OPTIONS = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
];

/** Columns available for expense list API filters (same semantics as Products column filters) */
const EXPENSE_FILTER_COLUMNS = [
    { key: "type", label: "Type", type: "dropdown" },
    { key: "name", label: "Name", type: "contains_text" },
    { key: "category", label: "Category", type: "dropdown" },
    { key: "show_under_advertising_cost", label: "Advertising Cost", type: "dropdown" },
    { key: "products", label: "Product", type: "product" },
];

function displayFilterChipValue(filter) {
    if (filter.column === "show_under_advertising_cost") {
        return filter.value === "1" || filter.value === 1 ? "Yes" : "No";
    }
    return String(filter.value ?? "");
}

/**
 * Modal UX aligned with dashboard `FilterPanel` (Products / Order Items):
 * applied filter chips, add column → condition → value, edit/remove per row.
 */
export default function ExpenseFilterModal({
    open,
    onClose,
    activeFilters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    categoryFilterOptions,
    productFilterOptions,
    productFilterLoading,
    searchProductsForFilter,
}) {
    const [newCol, setNewCol] = useState(null);
    const [newOp, setNewOp] = useState(null);
    const [newVal, setNewVal] = useState("");
    const [modalReady, setModalReady] = useState(false);
    const [focusGeneration, setFocusGeneration] = useState(0);
    const bumpFocus = useCallback(() => setFocusGeneration((g) => g + 1), []);

    useEffect(() => {
        if (!open) {
            setNewCol(null);
            setNewOp(null);
            setNewVal("");
        }
    }, [open]);

    const colDef = EXPENSE_FILTER_COLUMNS.find((c) => c.key === newCol);
    const existingFilter = activeFilters.find((f) => f.column === newCol);
    const isEditMode = !!existingFilter;
    const effectiveType = colDef?.type;
    const effectiveLabel = colDef?.label || newCol;

    const { formSectionRef, columnSelectRef, valueFieldRef } = useFilterFormAutoFocus({
        active: open && modalReady,
        newCol,
        newOp,
        effectiveType: effectiveType || "contains_text",
        focusGeneration,
    });

    const dropdownValueOptions = useMemo(() => {
        if (!newCol) return [];
        if (newCol === "type") return EXPENSE_TYPE_FILTER_OPTIONS;
        if (newCol === "category") return categoryFilterOptions || [];
        if (newCol === "show_under_advertising_cost") return AD_COST_OPTIONS;
        return [];
    }, [newCol, categoryFilterOptions]);

    const usedColumnKeys = useMemo(() => new Set(activeFilters.map((f) => f.column)), [activeFilters]);

    const handleColChange = (val) => {
        const existing = activeFilters.find((f) => f.column === val);
        const def = EXPENSE_FILTER_COLUMNS.find((c) => c.key === val);
        setNewCol(val);
        if (existing) {
            setNewOp(def?.type === "contains_text" ? "contains" : existing.operator);
            setNewVal(String(existing.value ?? ""));
        } else {
            if (def?.type === "dropdown" || def?.type === "product") setNewOp("eq");
            else if (def?.type === "contains_text") setNewOp("contains");
            else setNewOp(null);
            setNewVal("");
        }
        bumpFocus();
    };

    const handleSubmit = () => {
        if (!newCol || !newOp) return;
        if (newVal === "" || newVal === null || newVal === undefined) return;

        const value =
            effectiveType === "dropdown" || effectiveType === "product"
                ? newVal
                : effectiveType === "contains_text"
                    ? String(newVal).trim()
                    : String(newVal).trim();
        if (value === "") return;

        if (isEditMode) {
            updateFilter(existingFilter.id, { operator: newOp, value });
        } else {
            addFilter({
                id: `${newCol}_${Date.now()}`,
                column: newCol,
                columnLabel: effectiveLabel,
                type: effectiveType,
                operator: newOp,
                value,
            });
        }
        setNewCol(null);
        setNewOp(null);
        setNewVal("");
    };

    const canSubmit =
        newCol &&
        newOp &&
        newVal !== "" &&
        newVal !== null &&
        newVal !== undefined &&
        String(newVal).trim() !== "";

    return (
        <Modal
            className="expenses-filter-modal"
            open={open}
            afterOpenChange={setModalReady}
            onCancel={onClose}
            title={
                <div className="d-flex align-items-center gap-2">
                    <FilterOutlined style={{ color: "#f45b68" }} />
                    <span className="fw-bold expenses-filter-title-text">Column Filters</span>
                    {activeFilters.length > 0 && (
                        <span className="badge bg-danger ms-1 text-light" style={{ fontSize: 11 }}>
                            {activeFilters.length} Active
                        </span>
                    )}
                </div>
            }
            footer={
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span style={{ fontSize: 11, color: "var(--app-text-muted)" }} />
                    <div className="d-flex gap-2">
                        {activeFilters.length > 0 && (
                            <button type="button" className="btn btn-sm item-list-clearall-btn" onClick={clearFilters}>
                                Clear all filters
                            </button>
                        )}
                        <button type="button" className="btn btn-sm btn-danger" onClick={onClose}>
                            Done
                        </button>
                    </div>
                </div>
            }
            width={560}
            destroyOnClose={false}
            styles={{ body: { padding: "8px 0", maxHeight: "70vh", overflowY: "auto" } }}
        >
            {activeFilters.length > 0 && (
                <div style={{ padding: "0 24px 16px" }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            color: "var(--app-text-muted)",
                            marginBottom: 10,
                        }}
                    >
                        Applied Filters
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {activeFilters.map((f) => {
                            const isBeingEdited = newCol === f.column;
                            return (
                                <div
                                    key={f.id}
                                    className="d-flex align-items-center justify-content-between rounded-2 px-3 py-2"
                                    style={{
                                        background: isBeingEdited ? FILTER_MODAL_THEME.editRowBg : "color-mix(in srgb, #dc3545 8%, var(--app-surface))",
                                        border: isBeingEdited
                                            ? `1.5px solid ${FILTER_MODAL_THEME.editRowBorder}`
                                            : "1px solid color-mix(in srgb, #dc3545 28%, var(--app-surface))",
                                        transition: "background 0.2s, border-color 0.2s",
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="fw-semibold" style={{ fontSize: 13, color: "var(--app-text)" }}>
                                            {f.columnLabel}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                background: "var(--app-surface-alt)",
                                                color: "var(--app-text-muted)",
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {OPERATOR_SHORT[f.operator] || f.operator}
                                        </span>
                                        <span className="fw-bold" style={{ fontSize: 13, color: "#dc3545" }}>
                                            {displayFilterChipValue(f)}
                                        </span>
                                        {isBeingEdited && (
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    background: FILTER_MODAL_THEME.primary,
                                                    color: "#fff",
                                                    padding: "2px 8px",
                                                    borderRadius: 4,
                                                    fontWeight: 600,
                                                    letterSpacing: "0.02em",
                                                }}
                                            >
                                                editing
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn p-0 border-0 bg-transparent"
                                            style={{
                                                color: isBeingEdited ? FILTER_MODAL_THEME.primary : "var(--app-text-muted)",
                                                lineHeight: 1,
                                                fontSize: 15,
                                            }}
                                            onClick={() => handleColChange(f.column)}
                                            title="Edit this filter"
                                        >
                                            <EditOutlined />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn p-0"
                                            style={{ color: "var(--app-text-muted)", lineHeight: 1 }}
                                            onClick={() => {
                                                removeFilter(f.id);
                                                if (isBeingEdited) {
                                                    setNewCol(null);
                                                    setNewOp(null);
                                                    setNewVal("");
                                                }
                                            }}
                                            title="Remove this filter"
                                        >
                                            <DeleteOutlined style={{ fontSize: 14 }} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Divider style={{ margin: "16px 0 0" }} />
                </div>
            )}

            <div ref={formSectionRef} style={{ padding: "16px 24px 8px" }}>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: 14,
                        color: isEditMode ? FILTER_MODAL_THEME.primary : "var(--app-text-muted)",
                    }}
                >
                    {isEditMode ? (
                        <span className="d-inline-flex align-items-center gap-2">
                            <EditOutlined style={{ fontSize: 13 }} />
                            <span>Editing filter: {effectiveLabel}</span>
                        </span>
                    ) : activeFilters.length > 0 ? (
                        "Add Another Filter"
                    ) : (
                        "Add Filter"
                    )}
                </div>

                <div className="mb-3">
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--app-text)", marginBottom: 6 }}>1. Select Column</div>
                    <Select
                        ref={columnSelectRef}
                        placeholder="Choose a column to filter..."
                        style={{ width: "100%" }}
                        size="middle"
                        value={newCol}
                        onChange={handleColChange}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? "")
                                .toLowerCase()
                                .includes(String(input).toLowerCase())
                        }
                        options={EXPENSE_FILTER_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
                        allowClear
                        className="min-h-40px"
                        optionRender={(option) => (
                            <div className="d-flex align-items-center justify-content-between">
                                <span>{option.label}</span>
                                {usedColumnKeys.has(option.value) && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            background: option.value === newCol ? FILTER_MODAL_THEME.primary : "var(--app-surface-alt)",
                                            color: option.value === newCol ? "#fff" : "var(--app-text-muted)",
                                            padding: "2px 7px",
                                            borderRadius: 4,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {option.value === newCol ? "editing" : "filtered"}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                </div>

                {newCol && (newOp || effectiveType === "dropdown" || effectiveType === "product" || effectiveType === "contains_text") && (
                    <div className="mb-4">
                        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--app-text)", marginBottom: 6 }}>
                            {effectiveType === "dropdown" || effectiveType === "product"
                                ? "2. Select Value"
                                : effectiveType === "contains_text"
                                    ? "2. Enter text (contains)"
                                    : "3. Enter Value"}
                        </div>
                        {effectiveType === "dropdown" ? (
                            <Select
                                ref={valueFieldRef}
                                placeholder="Select a value..."
                                style={{ width: "100%" }}
                                size="middle"
                                value={newVal === "" ? undefined : newVal}
                                onChange={(val) => setNewVal(val ?? "")}
                                options={dropdownValueOptions}
                                className="min-h-40px"
                                notFoundContent={
                                    dropdownValueOptions.length === 0 ? (
                                        <span style={{ color: "var(--app-text-muted)", fontSize: 12 }}>No options available</span>
                                    ) : null
                                }
                            />
                        ) : effectiveType === "product" ? (
                            <Select
                                ref={valueFieldRef}
                                allowClear
                                showSearch
                                filterOption={false}
                                placeholder="Search and select a product (ASIN)"
                                style={{ width: "100%" }}
                                size="middle"
                                className="min-h-40px"
                                value={newVal === "" ? undefined : newVal}
                                loading={productFilterLoading}
                                options={productFilterOptions}
                                optionFilterProp="label"
                                onSearch={searchProductsForFilter}
                                onDropdownVisibleChange={(o) => {
                                    if (o) searchProductsForFilter("");
                                }}
                                onChange={(val) => setNewVal(val ?? "")}
                            />
                        ) : (
                            <Input
                                ref={valueFieldRef}
                                placeholder={
                                    effectiveType === "contains_text"
                                        ? "Text to search for in expense name…"
                                        : "Enter text..."
                                }
                                size="middle"
                                value={newVal}
                                onChange={(e) => setNewVal(e.target.value)}
                                onPressEnter={() => {
                                    if (canSubmit) handleSubmit();
                                }}
                            />
                        )}
                    </div>
                )}

                <button
                    type="button"
                    className={`fs-7 btn fw-bold w-100 d-flex align-items-center justify-content-center gap-2 ${isEditMode ? "" : "btn-danger"}`}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    style={{
                        padding: "9px 0",
                        opacity: canSubmit ? 1 : 0.45,
                        ...(isEditMode
                            ? {
                                  background: FILTER_MODAL_THEME.primary,
                                  border: `1px solid ${FILTER_MODAL_THEME.primary}`,
                                  color: "#fff",
                              }
                            : {}),
                    }}
                >
                    {isEditMode ? (
                        <>
                            <EditOutlined style={{ fontSize: 14 }} /> Update Filter
                        </>
                    ) : (
                        <>
                            <PlusOutlined /> Add Filter
                        </>
                    )}
                </button>

                {isEditMode && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link w-100 mt-2 border-0"
                        style={{ fontSize: 12, color: FILTER_MODAL_THEME.primary, opacity: 0.85 }}
                        onClick={() => {
                            setNewCol(null);
                            setNewOp(null);
                            setNewVal("");
                        }}
                    >
                        Cancel edit
                    </button>
                )}
            </div>
        </Modal>
    );
}
